import { Suspense, lazy, useEffect, useRef, useState, type ComponentType } from "react";

interface LazyOnViewProps {
  load: () => Promise<{ default: ComponentType<unknown> }>;
  rootMargin?: string;
  minHeight?: string;
  fallback?: React.ReactNode;
}

/**
 * Renders a lightweight placeholder until the section is near the viewport,
 * then dynamically imports and renders the real component. This keeps below-fold
 * chunks out of the initial dependency tree.
 */
const LazyOnView = ({
  load,
  rootMargin = "300px",
  minHeight = "40vh",
  fallback,
}: LazyOnViewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [Comp, setComp] = useState<ComponentType<unknown> | null>(null);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    let io: IntersectionObserver | null = null;
    const start = () => {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              setVisible(true);
              io?.disconnect();
              break;
            }
          }
        },
        { rootMargin }
      );
      io.observe(el);
    };
    // Defer observing until the browser is idle / after first paint so that
    // below-the-fold chunks never compete with the initial render.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    let idleId: number | null = null;
    let timeoutId: number | null = null;
    const schedule = () => {
      if (typeof w.requestIdleCallback === "function") {
        idleId = w.requestIdleCallback(start, { timeout: 1500 });
      } else {
        timeoutId = window.setTimeout(start, 400);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
    return () => {
      io?.disconnect();
      window.removeEventListener("load", schedule);
      if (idleId !== null && typeof (w as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback === "function") {
        (w as unknown as { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [visible, rootMargin]);

  useEffect(() => {
    if (!visible || Comp) return;
    let cancelled = false;
    const Lazy = lazy(load);
    // Trigger the dynamic import immediately once visible.
    load().then(() => {
      if (!cancelled) setComp(() => Lazy as unknown as ComponentType<unknown>);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, Comp, load]);

  return (
    <div ref={ref} style={{ minHeight: Comp ? undefined : minHeight }}>
      {Comp ? (
        <Suspense fallback={fallback ?? null}>
          <Comp />
        </Suspense>
      ) : null}
    </div>
  );
};

export default LazyOnView;